import React, { useState } from "react";
import { BankSelect } from "./components/BankSelect";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  PageTemplate,
  PageBody,
  PageFooter,
  Summary,
} from "../shared-components";
import { useIsYapilyDown } from "@flags";
import { formatSortCode } from "@utils";

const CopyableRow = ({ label, value, format, isLast }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`flex ${!isLast && "border-b"} justify-between border-zinc-200 text-sm py-5`}
    >
      <div>
        <p className="text-zinc-500 mb-1">{label}</p>
        <p className="font-medium">{format ? format(value) : value}</p>
      </div>
      <button
        onClick={handleCopy}
        className="text-primary font-medium cursor-pointer"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
};

const PRPayByBank = () => {
  const {
    statement,
    amount,
    merchant,
    reference,
    onBankPaymentConfirm,
    allInstitutions,
    institutionsLoading,
    institutionsError,
    summaryData,
    settlementDiscount,
  } = useOutletContext();

  const isYapilyDown = useIsYapilyDown();

  // TOTO: Ensure if card payment disabled, we don't show this page / redirects

  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");

  const [searchTerm, setSearchTerm] = useState("");
  const [processing, setProcessing] = useState(false);

  // Destructure summary data from context
  const {
    invoiceTotal,
    invoiceCount,
    creditNoteTotal,
    creditNoteCount,
    totalToPay,
  } = summaryData;

  const popular = [
    "barclays",
    "natwest",
    "lloyds",
    "hsbc_uk",
    "modelo-sandbox",
    "natwest-sandbox",
    "revolut-sandbox",
  ];

  // Filter and sort institutions based on search term
  const displayedInstitutions = allInstitutions
    ? [...allInstitutions]
        .filter(
          (x) =>
            !searchTerm ||
            x.name.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .sort((a, b) => popular.includes(b.id) - popular.includes(a.id))
    : [];

  const handleBankSelect = (institution) => {
    if (institution && !processing) {
      setProcessing(true);
      onBankPaymentConfirm?.(institution);
    }
  };

  const renderPageBodyContent = () => {
    if (isYapilyDown) {
      return (
        <div className="px-5 lg:px-0">
          <div className="flex flex-col items-center text-center py-8">
            {/* <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="w-7 h-7 text-amber-500" />
            </div> */}
            <h3 className="text-xl font-semibold mb-2">
              Pay By Bank temporarily unavailable
            </h3>
            <p className="text-sm text-zinc-500 max-w-md">
              Our pay by bank service provider is currently experiencing issues.
              You can still complete your payment via bank using the details
              below.
            </p>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <h4 className="text-sm font-semibold mb-1">Bank payment details</h4>
            <p className="text-xs text-zinc-500 border-b border-zinc-200 pb-4">
              Please use the reference below so we can match your payment
            </p>
            <CopyableRow label="Account name" value={merchant?.name} />
            <CopyableRow
              label="Account number"
              value={merchant?.accountNumber}
            />
            <CopyableRow
              label="Sort code"
              value={merchant?.sortCode}
              format={formatSortCode}
            />
            <CopyableRow label="Payment reference" value={reference} isLast />
          </div>
        </div>
      );
    }

    // Loading state
    if (institutionsLoading) {
      return (
        <div className="mb-5 px-5 lg:px-0">
          <h3 className="text-lg font-semibold">Select your bank</h3>
          <h4 className="text-sm text-zinc-500">Loading available banks...</h4>
        </div>
      );
    }

    // Error state
    if (institutionsError) {
      return (
        <div className="mb-5 px-5 lg:px-0">
          <h3 className="text-lg font-semibold">Select your bank</h3>
          <h4 className="text-sm text-red-500">
            Failed to load banks. Please refresh the page.
          </h4>
        </div>
      );
    }

    // Normal state
    return (
      <>
        <div className="mb-6 px-5 lg:px-0">
          <h3 className="text-xl/8 font-semibold">Select your bank</h3>
          <h4 className="text-sm text-zinc-700">
            Authorise the payment via online banking
          </h4>
        </div>

        <div className="px-5 lg:px-0">
          <BankSelect
            onSelect={handleBankSelect}
            displayedInstitutions={displayedInstitutions}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            processing={processing}
            error={error === "bank-error"}
          />
        </div>
      </>
    );
  };

  return (
    <PageTemplate className="flex flex-col lg:flex-row lg:h-screen lg:overflow-hidden">
      <PageBody>{renderPageBodyContent()}</PageBody>
      <PageFooter className="hidden lg:block">
        <div className="pb-7 space-y-2 hidden lg:block">
          <h2 className="text-xl font-medium">Payment summary</h2>
        </div>

        <Summary
          invoiceTotal={invoiceTotal}
          invoiceCount={invoiceCount}
          creditNoteTotal={creditNoteTotal}
          creditNoteCount={creditNoteCount}
          totalToPay={Math.max(0, totalToPay - settlementDiscount)}
          creditOnAccount={statement?.creditOnAccount || 0}
          hasStatement={!!statement}
          amountRequested={amount}
          settlementDiscount={settlementDiscount}
        />
      </PageFooter>
    </PageTemplate>
  );
};

export default PRPayByBank;
